/**
 * The data sources Explore can read, as the server lists them.
 *
 * Every source is listed, usable or not: an unavailable one is shown with the
 * sentence saying why ("Live data is not available right now."), never left out. The list
 * starts as `FALLBACK` - what the server said the day it was written - so the
 * Data source step draws before `/api/sources` answers, and still draws if it
 * never does.
 *
 * The id is what requests, links and stored charts carry. `DEFAULT_SOURCE` is
 * never written into them: absent means EOS, so everything saved before sources
 * existed reads the same data it always did. See `docs/plans/data-sources.md`.
 */

import { Atom } from './atom.ts';
import { hasFlag, type FeatureFlag } from './feature-flags.ts';
import { listSources } from '../api/endpoints.ts';
import type { DataSourceInfo } from '../api/types.ts';

/** What a request, link or stored chart that names no source reads. */
export const DEFAULT_SOURCE = 'historical';

const FALLBACK: DataSourceInfo[] = [
  {
    id: 'historical',
    label: 'EOS',
    description: "Historical DEC telemetry, one row per reading, back to each ship's first month.",
    available: true,
    reason: null,
    feature: null,
    capabilities: { history: true, streaming: false, cadence_s: null, max_window_s: null, legs: true, export: true, models: true },
  },
  {
    id: 'live',
    label: 'Live',
    description: 'Current telemetry, seconds old, over a rolling window.',
    available: false,
    reason: 'Live data is not available right now.',
    feature: 'live',
    capabilities: { history: false, streaming: true, cadence_s: null, max_window_s: null, legs: false, export: false, models: false },
  },
];

export const dataSources = new Atom<DataSourceInfo[]>(FALLBACK);

let loading: Promise<void> | null = null;

/** Ask the server once per page load; the fallback stays if it cannot answer. */
export function loadDataSources(): Promise<void> {
  loading ??= listSources()
    .then((list) => {
      if (Array.isArray(list) && list.length) dataSources.set(list);
    })
    .catch(() => {
      // The step still draws from FALLBACK; nothing here is worth a toast.
    });
  return loading;
}

/**
 * Whether Explore may switch to this source now: it is up, and its feature
 * (Live: `live`) is on for this account. The server enforces the same rule.
 */
export function isSelectable(source: DataSourceInfo): boolean {
  return source.available && featureOn(source);
}

/** Why a source cannot be chosen, for its hover; null when it can. */
export function unselectableReason(source: DataSourceInfo): string | null {
  if (!source.available) return source.reason ?? 'Not available.';
  if (!featureOn(source)) return `${source.label} data is not switched on for your account.`;
  return null;
}

function featureOn(source: DataSourceInfo): boolean {
  return !source.feature || hasFlag(source.feature as FeatureFlag);
}

/** Re-ask the server, e.g. after a source came back. */
export function reloadDataSources(): Promise<void> {
  loading = null;
  return loadDataSources();
}

/**
 * Whether the source keeps months of history (EOS) or only a rolling window
 * (Live). Long date presets, period comparison, Export and the Model Viewer
 * need history. Unknown ids are treated as having it, like EOS.
 */
export function sourceHasHistory(id: string | null | undefined): boolean {
  const src = dataSources.value.find((s) => s.id === (id || DEFAULT_SOURCE));
  return src ? src.capabilities.history : true;
}

/** `{ source }` for a request or stored recipe, or nothing for the default. */
export function sourceField(id: string | null | undefined): { source?: string } {
  return id && id !== DEFAULT_SOURCE ? { source: id } : {};
}

/** The name people see for a source id: "EOS" for `historical`. */
export function sourceLabel(id: string | null | undefined): string {
  const sid = id || DEFAULT_SOURCE;
  return dataSources.value.find((s) => s.id === sid)?.label ?? sid;
}
